import { Copy, ExternalLink, Pencil } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import { menuApi, useDraft, useImports, useMenus } from "../api/queries";
import { FlowSteps } from "../components/FlowSteps";
import { StatusChip, Switch } from "../components/kit";
import { useToast } from "../components/Toast";
import { Alert, ICON } from "../components/ui";
import { formatWhen, plural } from "../lib/format";
import type { ShellContext } from "./AppShell";
import { LaunchChecklist } from "./LaunchChecklist";
import { mainMenu, menuChip } from "./menus/status";
import { useRefreshMenus } from "./menus/useRefresh";
import { nextStep } from "./nextStep";
import { useAllergenDeadlines } from "./nutrition/AllergenBanner";

/** Prezentare generală (prototype `pgOverviewNew` / `pgOverviewActive`) — real data only, no demo figures. */
export function OverviewPage() {
  const { me, membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const menus = useMenus(r.id);
  const main = mainMenu(menus.data ?? []);
  const draft = useDraft(r.id, main?.id ?? null);
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshMenus(r.id);
  const first = me.user.name.split(" ")[0];
  const [copied, setCopied] = useState(false);
  const imports = useImports(r.id);
  const deadlines = useAllergenDeadlines(r.id);
  const step = nextStep({
    menus: menus.data ?? [],
    main,
    latestImport: imports.data?.[0],
    allergenDeadlines: deadlines.data?.length ?? 0,
    publicUrl: r.publicUrl,
  });
  const stepCard = step && (
    <section className={`card next-step next-${step.tone}`} aria-labelledby="ov-next">
      <div className="stack" style={{ gap: 4, flex: "1 1 220px", minWidth: 0 }}>
        <span className="caption muted">Următorul pas</span>
        <h2 id="ov-next" className="h2" style={{ margin: 0 }}>
          {step.title}
        </h2>
        <span className="page-sub">{step.body}</span>
      </div>
      {step.external ? (
        <a className="btn btn-primary" href={step.to} target="_blank" rel="noopener">
          <ExternalLink {...ICON} aria-hidden="true" />
          {step.action}
        </a>
      ) : (
        <Link className="btn btn-primary" to={step.to}>
          {step.action}
        </Link>
      )}
    </section>
  );

  if (menus.isLoading) {
    return (
      <p className="muted" role="status">
        Se încarcă…
      </p>
    );
  }
  if (menus.isError) {
    return (
      <Alert tone="error" live>
        {menus.error.message}
      </Alert>
    );
  }

  if (!main) {
    if (step) {
      return (
        <>
          <div className="stack" style={{ gap: 6 }}>
            <span className="greet">Bun venit, {first}</span>
            <h1 className="h1">{r.name}</h1>
          </div>
          {stepCard}
        </>
      );
    }
    return (
      <>
        <div className="stack" style={{ gap: 6 }}>
          <span className="greet">Bun venit, {first}</span>
          <h1 className="h1">Hai să punem meniul {r.name} online</h1>
          <span className="page-sub" style={{ fontSize: 15, lineHeight: "22px", maxWidth: 640 }}>
            Trei pași. Nimic nu devine public până nu apeși „Publică meniul”. Aspectul îl alegi oricând după.
          </span>
        </div>
        <FlowSteps current={0} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: 16,
          }}
        >
          <div
            className="stack"
            style={{
              background: "var(--color-selected)",
              border: "1.5px dashed var(--color-selected-line)",
              borderRadius: 20,
              padding: 28,
              gap: 12,
            }}
          >
            <span className="badge-accent">Recomandat</span>
            <span className="h2">Încarcă meniul existent</span>
            <span className="muted" style={{ fontSize: 15, lineHeight: "22px" }}>
              Încarci un PDF sau fotografii ale meniului. Pregătim categoriile, produsele și prețurile; tu
              verifici doar ce e marcat și publici.
            </span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start" }}
              onClick={() => navigate("/app/imports")}
            >
              Încarcă meniul
            </button>
          </div>
          <div className="card stack" style={{ gap: 12, padding: 28 }}>
            <span className="h2">Creează meniul manual</span>
            <span className="muted" style={{ fontSize: 15, lineHeight: "22px" }}>
              Pornești de la un meniu gol și adaugi categoriile și produsele unul câte unul. Potrivit pentru
              meniuri scurte.
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ alignSelf: "flex-start" }}
              onClick={async () => {
                const m = await menuApi(r.id).create("Meniul principal");
                await refresh(m.id);
                toast("Meniul principal a fost creat ca ciornă. Adaugă prima categorie.");
                navigate(`/app/products?menu=${m.id}`);
              }}
            >
              Creează meniul
            </button>
          </div>
        </div>
        <Alert tone="info">
          Linkul tău public va fi{" "}
          <strong style={{ fontWeight: 600 }}>{r.publicUrl.replace(/^https?:\/\//, "")}</strong>. Codul QR
          apare după prima publicare și nu se schimbă la actualizări.
        </Alert>
      </>
    );
  }

  const d = draft.data;
  const unconfirmed = d?.products.filter((p) => !p.allergensConfirmed).length ?? 0;
  const quick = (d?.products ?? []).slice(0, 6);
  const langs = d?.products.some((p) => p.translations.en?.name) ? 2 : 1;

  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <span className="greet">Bună ziua, {first}</span>
          <h1 className="h1" style={{ lineHeight: 1.2 }}>
            {r.name}
          </h1>
        </div>
      </div>

      {stepCard}

      <LaunchChecklist rid={r.id} menus={menus.data ?? []} />

      <section className="card stack" aria-labelledby="ov-menu" style={{ gap: 18 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div className="stack" style={{ gap: 6 }}>
            <h2 id="ov-menu" className="h2">
              {main.name}
            </h2>
            <span className="page-sub">
              {main.publishedAt ? `Publicat ${formatWhen(main.publishedAt)}` : "Nepublicat"}
              {unconfirmed
                ? ` · ${plural(unconfirmed, "produs fără informații despre alergeni", "produse fără informații despre alergeni")}`
                : ""}
            </span>
          </div>
          <StatusChip {...menuChip(main)} large />
        </div>
        <div className="stat-grid">
          {[
            [d?.categories.length ?? main.categoryCount, "Categorii"],
            [d?.products.length ?? main.productCount, "Produse"],
            [d?.products.filter((p) => !p.available).length ?? 0, "Indisponibile azi"],
            [langs, langs === 1 ? "Limbă" : "Limbi"],
          ].map(([v, k]) => (
            <div className="stat" key={k}>
              <span className="v">{v}</span>
              <span className="k">{k}</span>
            </div>
          ))}
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/app/products?menu=${main.id}`)}
          >
            <Pencil {...ICON} aria-hidden="true" />
            Editează meniul
          </button>
          {main.publicVisible && (
            <a className="btn btn-secondary btn-sm" href={r.publicUrl} target="_blank" rel="noopener">
              <ExternalLink {...ICON} aria-hidden="true" />
              Vezi meniul public
            </a>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(r.publicUrl);
                setCopied(true);
                toast(`Link copiat: ${r.publicUrl.replace(/^https?:\/\//, "")}`);
              } catch {
                setCopied(false);
              }
            }}
          >
            <Copy {...ICON} aria-hidden="true" />
            {copied ? "Link copiat" : "Copiază linkul"}
          </button>
        </div>
      </section>

      {quick.length > 0 && (
        <section className="card stack" aria-labelledby="ov-av" style={{ gap: 8 }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 id="ov-av" className="h2">
              Disponibil azi
            </h2>
            <button
              type="button"
              className="btn-link"
              onClick={() => navigate(`/app/products?menu=${main.id}`)}
            >
              Toate produsele ({d?.products.length ?? 0})
            </button>
          </div>
          <span className="page-sub">Se aplică imediat în meniul public. Poți anula din notificare.</span>
          {quick.map((p) => (
            <div
              key={p.id}
              className="row"
              style={{ gap: 12, minHeight: 52, borderTop: "1px solid var(--color-border)" }}
            >
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                <span
                  style={{
                    fontWeight: 600,
                    color: p.available ? "var(--color-text)" : "var(--color-text-muted)",
                  }}
                >
                  {p.name}
                </span>
                <span
                  className="caption"
                  style={{ color: p.available ? "var(--color-success)" : "var(--color-error)" }}
                >
                  {p.available ? "Disponibil" : "Indisponibil azi"}
                </span>
              </div>
              <Switch
                checked={p.available}
                label={`${p.name}: disponibil`}
                onChange={async () => {
                  const api = menuApi(r.id);
                  const res = await api.setAvailability(p.id, !p.available);
                  await refresh(main.id);
                  toast(
                    `${p.name}: ${res.available ? "disponibil din nou" : "indisponibil azi"}. Apare imediat în meniul public.`,
                    async () => {
                      await api.setAvailability(p.id, res.previous);
                      await refresh(main.id);
                    },
                  );
                }}
              />
            </div>
          ))}
        </section>
      )}
    </>
  );
}
